import { Avatar } from '@mantine/core'
import { useLocation, useNavigate } from 'react-router-dom'
import { NavGroup, NavItem, LighthouseMark } from '@lighthouse/design'
import { NAV_SECTIONS, findNavEntry } from '../nav/navModel'
import type { NavEntry } from '../nav/navModel'
import { useNavBadges, useNavUnmeasuredBadges } from '../nav/useNavBadges'
import { showComingSoon, showHangfireUnconfigured, showRestricted } from '../utils/comingSoon'
import { hangfireDashboardUrl } from '../utils/hangfireUrl'
import { ADMIN_ROLE_REQUIRED } from '../utils/format'
import { useLighthouseAdmin } from '../hooks/useLighthouseAdmin'
import type { AdminGate } from '../utils/adminGate'
import { useCurrentUser, useProfilePhoto } from '../auth'

/**
 * The role line under the signed-in user, from the SERVER's answer.
 *
 * It used to print a literal "Administrator" for every principal, API-key
 * sessions included. Now: admin -> "Administrator", denied -> "Read-only",
 * unknown -> nothing (no claim is better than a wrong one). In Development the
 * policy degrades to "any authenticated user", so everyone reads Administrator
 * there — that is the server's own answer, and correct to show.
 */
function roleLine(gate: AdminGate): string | null {
  if (gate.state === 'admin') return 'Administrator'
  if (gate.state === 'denied') return 'Read-only'
  return null
}

function getInitials(name: string): string {
  return name.split(' ').filter(Boolean).map((p) => p[0]).join('').toUpperCase().slice(0, 2)
}

export function AppNavbar() {
  const location = useLocation()
  const navigate = useNavigate()
  const badges = useNavBadges()
  const unmeasured = useNavUnmeasuredBadges()
  const user = useCurrentUser()
  const profilePhoto = useProfilePhoto()
  const gate = useLighthouseAdmin()
  const role = roleLine(gate)

  // A Lighthouse.Admin row the server says this caller cannot open: dimmed, and it
  // explains itself instead of routing to a page that would only 403.
  const isRestricted = (entry: NavEntry) => entry.requires === 'lighthouseAdmin' && gate.state === 'denied'

  // Longest-path match, so /admin/data-entry/audit highlights Audit Log rather
  // than Data Entry Admin.
  const activePath = findNavEntry(location.pathname)?.entry.path

  const handleClick = (entry: NavEntry) => {
    if (entry.state === 'coming-soon') {
      showComingSoon(entry.label, entry.comingSoonHint)
      return
    }
    if (isRestricted(entry)) {
      showRestricted(entry.label, gate.state === 'denied' ? gate.reason : null)
      return
    }
    if (entry.externalPath) {
      // Never a relative '/hangfire': on Static Web Apps that resolves into this
      // SPA, which has no such route. An unconfigured build says so instead.
      const url = hangfireDashboardUrl()
      if (url === null) {
        showHangfireUnconfigured()
        return
      }
      window.open(url, '_blank', 'noopener,noreferrer')
      return
    }
    if (entry.path) navigate(entry.path)
  }

  const renderItem = (entry: NavEntry) => {
    const Icon = entry.icon
    const restricted = isRestricted(entry)
    return (
      <NavItem
        key={entry.label}
        label={entry.label}
        icon={<Icon size={16} stroke={1.9} />}
        active={!entry.externalPath && entry.path !== undefined && entry.path === activePath}
        // No badge on a restricted row: it would count something this caller cannot open.
        badge={entry.badgeKey && !restricted ? badges[entry.badgeKey] : undefined}
        // Only the signal queue earns the critical tone: it counts things that are
        // wrong right now. A governance backlog is work, not an emergency, and
        // spending the loudest colour in the system on it teaches operators to
        // ignore it.
        badgeTone={entry.badgeKey === 'signalInbox' ? 'critical' : 'muted'}
        // And because it is the critical-toned row, a count nobody could take
        // there is a muted dash with the reason, never a quiet rail — which on
        // this row would read as "nothing is wrong".
        badgeUnmeasured={entry.badgeKey && !restricted ? unmeasured[entry.badgeKey] : undefined}
        // 'coming-soon' is the design system's only dimmed, aria-disabled form, so a
        // restricted row borrows it — with its own hint, and its own click handler.
        state={restricted ? 'coming-soon' : entry.state}
        comingSoonHint={restricted ? ADMIN_ROLE_REQUIRED : entry.comingSoonHint}
        external={Boolean(entry.externalPath)}
        title={entry.subtitle}
        onClick={() => handleClick(entry)}
      />
    )
  }

  return (
    <div className="lh-rail">
      <div className="lh-rail-brand">
        <span className="lh-rail-brand-mark">
          <LighthouseMark size={26} title="Observatory" />
        </span>
        <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <span className="lh-rail-brand-name">Observatory</span>
          <span className="lh-rail-brand-sub">Lighthouse Admin Command Center</span>
        </span>
      </div>

      <nav className="lh-rail-scroll" aria-label="Main">
        {NAV_SECTIONS.map((section, i) =>
          section.label ? (
            <NavGroup key={section.label} label={section.label} defaultOpen={section.defaultOpen ?? true}>
              {section.items.map(renderItem)}
            </NavGroup>
          ) : (
            // The top-level entry (Overview) sits outside any group.
            <div className="lh-nav-list" key={`top-${i}`}>{section.items.map(renderItem)}</div>
          ),
        )}
      </nav>

      {user && (
        <div className="lh-rail-admin">
          <span className="lh-rail-admin-label">Administration</span>
          <div className="lh-rail-admin-user">
            <Avatar color="sea" radius="xl" size={30} src={profilePhoto}>
              {getInitials(user.name)}
            </Avatar>
            <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
              <span className="lh-rail-admin-name">{user.name}</span>
              {role && <span className="lh-rail-admin-role">{role}</span>}
            </span>
          </div>
        </div>
      )}

      <div className="lh-rail-illustration" aria-hidden="true" />
    </div>
  )
}
